import { CustomAction } from '@thoughtspot/blink-context';
import { useEffect } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { useGetUserDetailsByIdLazyQuery } from '/@services/user-group/user-group-service';
import { CodeBasedCustomActionErrorType } from '/@utils/embed-base.util';
import { filterActionsByProperty } from './custom-actions.util';

interface UseCodeBasedCustomActionsFilterProps {
    codeBasedCustomActions: CustomAction[];
    dispatchEmbedEvent: (eventType: string, payload: any) => void;
}

export const useCodeBasedCustomActionsFilter = ({
    codeBasedCustomActions,
    dispatchEmbedEvent,
}: UseCodeBasedCustomActionsFilterProps) => {
    const { sessionService } = useAppContext();
    const { getCurrentOrgId, getUserGuid } = sessionService;
    const currentOrgId = getCurrentOrgId();
    const userGuid = getUserGuid();
    const { data, getUserById } = useGetUserDetailsByIdLazyQuery([userGuid]);

    // Filter code-based actions by organization
    const filterCodeBasedCustomActionsByOrg = filterActionsByProperty(
        codeBasedCustomActions || [],
        'orgIds',
        [currentOrgId?.toString() || ''],
        {
            emptyErrorType: CodeBasedCustomActionErrorType.OrgIdsEmpty,
            noMatchErrorType: CodeBasedCustomActionErrorType.OrgIdNotAllowed,
        },
        dispatchEmbedEvent,
        {
            currentOrgId: currentOrgId?.toString() || '',
        },
    );

    // Check if any actions have groupIds that require API call
    const hasActionsWithGroupIds = filterCodeBasedCustomActionsByOrg.some(
        action => action.groupIds && action.groupIds.length > 0,
    );

    useEffect(() => {
        if (hasActionsWithGroupIds && userGuid) {
            getUserById();
        }
    }, [hasActionsWithGroupIds, userGuid, getUserById]);

    // Filter code-based actions by group
    const filterCodeBasedCustomActionsByGroup = (() => {
        // If no actions require group filtering, return the org-filtered actions
        if (!hasActionsWithGroupIds) {
            return filterCodeBasedCustomActionsByOrg;
        }

        // If we haven't loaded user data yet, hide actions that require group check
        if (!data?.getUserDetailById) {
            return filterCodeBasedCustomActionsByOrg.filter(
                action => !action.groupIds,
            );
        }

        // Get user groups from the API response
        const userGroups = data?.getUserDetailById?.[0]?.inheritedGroups || [];

        return filterActionsByProperty(
            filterCodeBasedCustomActionsByOrg,
            'groupIds',
            userGroups,
            {
                emptyErrorType: CodeBasedCustomActionErrorType.GroupIdsEmpty,
                noMatchErrorType:
                    CodeBasedCustomActionErrorType.GroupIdNotMatched,
            },
            dispatchEmbedEvent,
            {
                userGroups: userGroups.join(', '),
            },
        );
    })();

    return {
        filteredCodeBasedCustomActions: filterCodeBasedCustomActionsByGroup,
        hasActionsWithGroupIds,
        isLoadingUserData: hasActionsWithGroupIds && !data?.getUserDetailById,
    };
};
